/**
 * Compliance Entity - RTK Query API
 * API endpoints for Compliance domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

// CMP-10: Obligation.STATUS_CHOICES has no 'pending'/'in_progress' slugs --
// the previous mapping sent those literal, nonexistent values to the
// backend and got zero results back for every "Pending"/"In Progress"
// filter. "Overdue" is not a status at all: it's the due_date-based
// ObligationViewSet.get_queryset() `overdue=true` param, independent of
// status. This maps each UI filter label to the query param(s) that
// actually match the backend's real semantics:
//   Pending      -> status=identified (registered/assessed haven't happened yet)
//   In Progress  -> status=registered,assessed (mid-workflow, comma-separated
//                   list now supported by ObligationViewSet.get_queryset())
//   Compliant    -> status=compliant (unchanged, already correct)
//   Overdue      -> overdue=true (due_date < today), not a status filter
export const complianceStatusFilterToQueryParams = (statusLabel) => {
  if (statusLabel === 'Overdue') {
    return { overdue: 'true' };
  }
  const statusMap = {
    Pending: 'identified',
    'In Progress': 'registered,assessed',
    Compliant: 'compliant',
  };
  return { status: statusMap[statusLabel] || statusLabel };
};

// CMP-07: lifted out of the getObligationAvailableTransitions/
// getObligationTransitionHistory builder.query definitions so their
// behavior is unit-testable directly (see governanceApiTestUtils in
// governanceApi.js for the precedent this mirrors) instead of only being
// pinned by source-text assertions on the endpoint's URL string.
const transformAvailableTransitions = (response) =>
  (response?.available_transitions || []).map((to_state) => ({
    to_state,
    required_evidence: false,
  }));

// NEW-RSK-03 renamed WorkflowTransitionLogSerializer's exposed fields from
// triggered_by/triggered_at to transitioned_by/transitioned_at (see
// apps/shared/workflow/serializers.py), but WorkflowPanel's HistoryRow reads
// transitioned_by/transitioned_at -- this always read the old triggered_by/
// triggered_at names off the raw response, so every obligation's transition
// history rendered a blank actor and blank date. Mirrors the fallback in
// riskApi.js's getRiskTransitionHistory transformResponse (same
// WorkflowService-backed serializer): prefer the new field name, fall back
// to the pre-rename name so history still renders against either shape.
const transformTransitionHistory = (response) =>
  (response || []).map((entry) => ({
    from_state: entry.from_state,
    to_state: entry.to_state,
    comment: entry.comment,
    transitioned_by: entry.transitioned_by ?? entry.triggered_by ?? null,
    transitioned_at: entry.transitioned_at ?? entry.triggered_at ?? null,
  }));

// CMP-21/22: list params without empty filters (an empty `status=` would
// filter everything out on the backend).
const buildListParams = (page, limit, filters = {}) => {
  const params = { page, page_size: limit };
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      params[key] = value;
    }
  });
  return params;
};

// CMP-21 (D7): evidence is multipart. Empty values are left out: an empty
// string is not a valid (nullable) obligation / control_test id.
const buildEvidenceFormData = (data) => {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    form.append(key, value);
  });
  return form;
};

const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

// The notified actions take a multipart body (optional proof file). Empty values are
// skipped; the browser sets the Content-Type boundary.
const buildBreachNotifiedFormData = (data) => {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'proof') {
      if (typeof File !== 'undefined' && value instanceof File) form.append(key, value);
      return;
    }
    form.append(key, value);
  });
  return form;
};

export const complianceApiTestUtils = {
  buildListParams,
  buildEvidenceFormData,
  buildBreachNotifiedFormData,
  transformAvailableTransitions,
  transformTransitionHistory,
};

export const complianceApi = createApi({
  reducerPath: 'complianceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Obligation',
    'Framework',
    'ControlTest',
    'Evidence',
    'Attestation',
    'Exception',
    'ComplianceStats',
    'ProcessingActivity',
    'DPIA',
    'Dsar',
    'Breach',
    'WorkflowTransition',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // Obligations
    // =========================================================================
    getObligations: builder.query({
      query: ({ filters = {}, page = 1, limit = 20, ordering = '-created_at' } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        params.set('ordering', ordering);

        if (filters.status && filters.status !== 'all') {
          const statusParams = complianceStatusFilterToQueryParams(filters.status);
          Object.entries(statusParams).forEach(([key, value]) => params.set(key, value));
        }
        if (filters.framework) {
          params.set('regulation', filters.framework);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/compliance/obligations/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Obligation', id })),
              { type: 'Obligation', id: 'LIST' },
            ]
          : [{ type: 'Obligation', id: 'LIST' }],
    }),

    getObligationById: builder.query({
      query: (id) => ({
        url: `/api/v1/compliance/obligations/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Obligation', id }],
    }),

    createObligation: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/obligations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Obligation', id: 'LIST' }, 'ComplianceStats'],
    }),

    updateObligation: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/compliance/obligations/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Obligation', id },
        { type: 'Obligation', id: 'LIST' },
        'ComplianceStats',
      ],
    }),

    deleteObligation: builder.mutation({
      query: (id) => ({
        url: `/api/v1/compliance/obligations/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Obligation', id: 'LIST' }, 'ComplianceStats'],
    }),

    // =========================================================================
    // Frameworks
    // =========================================================================
    getFrameworks: builder.query({
      query: () => ({
        url: '/api/v1/compliance/regulations/',
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: ['Framework'],
    }),

    getFrameworkById: builder.query({
      query: (id) => ({
        url: `/api/v1/compliance/regulations/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Framework', id }],
    }),

    createFramework: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/regulations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Framework'],
    }),

    // =========================================================================
    // Control Tests
    // =========================================================================
    // CMP-22: the backend filters on obligation_id / control_id (and
    // result); this used to send `control`, which was silently ignored.
    getControlTests: builder.query({
      query: ({ filters = {}, page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/compliance/control-tests/',
        params: buildListParams(page, limit, {
          obligation_id: filters.obligation,
          control_id: filters.control,
          result: filters.result,
        }),
      }),
      transformResponse: normalizeListResponse,
      providesTags: ['ControlTest'],
    }),

    createControlTest: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/control-tests/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ControlTest', 'ComplianceStats'],
    }),

    // =========================================================================
    // Evidence (CMP-21, D7)
    // =========================================================================
    getObligationEvidence: builder.query({
      query: (obligationId) => ({
        url: `/api/v1/compliance/obligations/${obligationId}/evidence/`,
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: (result, error, obligationId) => [{ type: 'Evidence', id: obligationId }],
    }),

    // Multipart: the browser sets Content-Type (with the boundary).
    uploadEvidence: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/evidence/',
        method: 'POST',
        body: buildEvidenceFormData(data),
      }),
      invalidatesTags: (result, error, { obligation }) => [
        { type: 'Evidence', id: obligation },
        'ControlTest',
      ],
    }),

    deleteEvidence: builder.mutation({
      // D24: archives; an optional reason is sent in the request body.
      query: ({ id, reason }) => ({
        url: `/api/v1/compliance/evidence/${id}/`,
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
      invalidatesTags: (result, error, { obligation }) => [{ type: 'Evidence', id: obligation }],
    }),

    // The file is streamed by the tenant-scoped download action (never a
    // storage URL), fetched as a blob with the auth header and saved locally.
    downloadEvidence: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/compliance/evidence/${id}/download/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'evidence';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    // =========================================================================
    // Attestations (CMP-22, D8)
    // =========================================================================
    // Writers get the tenant's attestations; everyone else only theirs.
    getAttestations: builder.query({
      query: ({ page = 1, limit = 20, status, obligation } = {}) => ({
        url: '/api/v1/compliance/attestations/',
        params: buildListParams(page, limit, { status, obligation_id: obligation }),
      }),
      transformResponse: normalizeListResponse,
      providesTags: ['Attestation'],
    }),

    getMyAttestations: builder.query({
      query: ({ page = 1, limit = 50, status } = {}) => ({
        url: '/api/v1/compliance/attestations/mine/',
        params: buildListParams(page, limit, { status }),
      }),
      transformResponse: normalizeListResponse,
      providesTags: ['Attestation'],
    }),

    createAttestation: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/attestations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Attestation'],
    }),

    completeAttestation: builder.mutation({
      query: ({ id, comments = '' }) => ({
        url: `/api/v1/compliance/attestations/${id}/complete/`,
        method: 'POST',
        body: { comments },
      }),
      invalidatesTags: ['Attestation'],
    }),

    declineAttestation: builder.mutation({
      query: ({ id, comments = '' }) => ({
        url: `/api/v1/compliance/attestations/${id}/decline/`,
        method: 'POST',
        body: { comments },
      }),
      invalidatesTags: ['Attestation'],
    }),

    // =========================================================================
    // Exceptions (CMP-22, D9)
    // =========================================================================
    getExceptions: builder.query({
      query: ({
        page = 1,
        limit = 20,
        status,
        affected_unit: affectedUnit,
        owning_unit: owningUnit,
      } = {}) => ({
        url: '/api/v1/compliance/exceptions/',
        params: buildListParams(page, limit, {
          status,
          affected_unit: affectedUnit,
          owning_unit: owningUnit,
        }),
      }),
      transformResponse: normalizeListResponse,
      providesTags: ['Exception'],
    }),

    // Detail carries approval_request_id/approval_status (ruling R19: not on list rows).
    getException: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'Exception', id }],
    }),

    createException: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/exceptions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Exception'],
    }),

    updateException: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Exception'],
    }),

    // Approval routing C1: send a returned exception back for approval.
    resubmitException: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/resubmit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: ['Exception'],
    }),

    // Spec §8.6: a returned exception can be withdrawn instead of resubmitted.
    withdrawException: builder.mutation({
      query: (id) => ({
        url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/withdraw/`,
        method: 'POST',
      }),
      invalidatesTags: ['Exception'],
    }),

    approveException: builder.mutation({
      query: (id) => ({
        url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/approve/`,
        method: 'POST',
      }),
      invalidatesTags: ['Exception'],
    }),

    rejectException: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/v1/compliance/exceptions/${encodeURIComponent(id)}/reject/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Exception'],
    }),

    revokeException: builder.mutation({
      query: (id) => ({
        url: `/api/v1/compliance/exceptions/${id}/revoke/`,
        method: 'POST',
      }),
      invalidatesTags: ['Exception'],
    }),

    // =========================================================================
    // Statistics
    // =========================================================================
    getComplianceSummary: builder.query({
      query: () => ({
        url: '/api/v1/compliance/obligations/summary/',
      }),
      providesTags: ['ComplianceStats'],
    }),

    // =========================================================================
    // PROCESSING ACTIVITIES (GDPR Art.30)
    // =========================================================================
    getProcessingActivities: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/compliance/processing-activities/',
        params: { page, page_size: limit },
      }),
      transformResponse: (response) => ({
        data: response?.results || response || [],
        total: response?.count || 0,
      }),
      providesTags: [{ type: 'ProcessingActivity', id: 'LIST' }],
    }),

    createProcessingActivity: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/processing-activities/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ProcessingActivity', id: 'LIST' }],
    }),

    // =========================================================================
    // DSAR REGISTER (D2b): named DPOs and tenant admins only; others get 404 / empty.
    // The list carries a masked requester only; the detail carries the full fields.
    // =========================================================================
    getDsars: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => ({
        url: '/api/v1/compliance/dsars/',
        params: { page, page_size: limit, ...(status ? { status } : {}) },
      }),
      transformResponse: (response) => ({
        data: response?.results || (Array.isArray(response) ? response : []),
        total: response?.count || 0,
      }),
      providesTags: [{ type: 'Dsar', id: 'LIST' }],
    }),

    getDsar: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/dsars/${encodeURIComponent(String(id))}/` }),
      providesTags: (result, error, id) => [{ type: 'Dsar', id }],
    }),

    createDsar: builder.mutation({
      query: (body) => ({ url: '/api/v1/compliance/dsars/', method: 'POST', body }),
      invalidatesTags: [{ type: 'Dsar', id: 'LIST' }],
    }),

    updateDsar: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/compliance/dsars/${encodeURIComponent(String(id))}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Dsar', id },
        { type: 'Dsar', id: 'LIST' },
      ],
    }),

    ...Object.fromEntries(
      ['verify', 'extend', 'respond', 'refuse', 'withdraw'].map((action) => [
        `${action}Dsar`,
        builder.mutation({
          query: ({ id, ...body }) => ({
            url: `/api/v1/compliance/dsars/${encodeURIComponent(String(id))}/${action}/`,
            method: 'POST',
            body,
          }),
          invalidatesTags: (result, error, { id }) => [
            { type: 'Dsar', id },
            { type: 'Dsar', id: 'LIST' },
          ],
        }),
      ])
    ),

    // Correct a mis-keyed receipt time: reason required, open and not extended only;
    // the backend audits it and moves the deadline.
    correctDsarReceivedAt: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/compliance/dsars/${encodeURIComponent(String(id))}/correct-received-at/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Dsar', id },
        { type: 'Dsar', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // PERSONAL-DATA BREACHES (D2b): named DPOs and tenant admins only. The incident
    // reporter may create a record for their own incident and gets back only its id
    // and reference. Each statutory step is its own action; PATCH is assessment only.
    // =========================================================================
    getBreaches: builder.query({
      query: ({ page = 1, limit = 20, incident } = {}) => ({
        url: '/api/v1/compliance/breaches/',
        params: { page, page_size: limit, ...(incident ? { incident } : {}) },
      }),
      transformResponse: (response) => ({
        data: response?.results || (Array.isArray(response) ? response : []),
        total: response?.count || 0,
      }),
      providesTags: [{ type: 'Breach', id: 'LIST' }],
    }),

    getBreach: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/breaches/${encodeURIComponent(String(id))}/` }),
      providesTags: (result, error, id) => [{ type: 'Breach', id }],
    }),

    // The incident's own detail (has_breach_record) is refetched by the caller on success;
    // it lives in the risk API, which this module deliberately does not import.
    createBreach: builder.mutation({
      query: (body) => ({ url: '/api/v1/compliance/breaches/', method: 'POST', body }),
      invalidatesTags: [{ type: 'Breach', id: 'LIST' }],
    }),

    updateBreach: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/compliance/breaches/${encodeURIComponent(String(id))}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Breach', id },
        { type: 'Breach', id: 'LIST' },
      ],
    }),

    ...Object.fromEntries(
      [
        ['decideRegulator', 'decide-regulator'],
        ['decideSubjects', 'decide-subjects'],
        ['setAwareAt', 'aware-at'],
      ].map(([name, path]) => [
        name,
        builder.mutation({
          query: ({ id, ...body }) => ({
            url: `/api/v1/compliance/breaches/${encodeURIComponent(String(id))}/${path}/`,
            method: 'POST',
            body,
          }),
          invalidatesTags: (result, error, { id }) => [
            { type: 'Breach', id },
            { type: 'Breach', id: 'LIST' },
          ],
        }),
      ])
    ),

    ...Object.fromEntries(
      [
        ['recordRegulatorNotified', 'regulator-notified'],
        ['recordSubjectsNotified', 'subjects-notified'],
      ].map(([name, path]) => [
        name,
        builder.mutation({
          query: ({ id, ...body }) => ({
            url: `/api/v1/compliance/breaches/${encodeURIComponent(String(id))}/${path}/`,
            method: 'POST',
            body: buildBreachNotifiedFormData(body),
          }),
          invalidatesTags: (result, error, { id }) => [
            { type: 'Breach', id },
            { type: 'Breach', id: 'LIST' },
          ],
        }),
      ])
    ),

    // Proof file streamed by the tenant-scoped action, fetched with the auth header.
    downloadBreachProof: builder.mutation({
      async queryFn({ id, which, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/compliance/breaches/${encodeURIComponent(String(id))}/proof/${which}/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'proof';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    // =========================================================================
    // DPIAS (GDPR Art.35)
    // =========================================================================
    getDPIAs: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/compliance/dpias/',
        params: { page, page_size: limit },
      }),
      transformResponse: (response) => ({
        data: response?.results || response || [],
        total: response?.count || 0,
      }),
      providesTags: [{ type: 'DPIA', id: 'LIST' }],
    }),

    // Detail carries approval_request_id/approval_status (ruling R19: not on list rows).
    getDPIA: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/dpias/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'DPIA', id }],
    }),

    // Approval routing C1: draft/in_progress -> review (also resubmits a returned round).
    submitDPIA: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/compliance/dpias/${encodeURIComponent(id)}/submit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'DPIA', id },
        { type: 'DPIA', id: 'LIST' },
      ],
    }),

    createDPIA: builder.mutation({
      query: (data) => ({
        url: '/api/v1/compliance/dpias/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'DPIA', id: 'LIST' }],
    }),

    // A draft or in-progress DPIA (including one returned for correction) is edited
    // in place; the server locks content while its approval request is open.
    updateDPIA: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/compliance/dpias/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'DPIA', id },
        { type: 'DPIA', id: 'LIST' },
      ],
    }),

    approveDPIA: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/compliance/dpias/${encodeURIComponent(id)}/approve/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'DPIA', id },
        { type: 'DPIA', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // Workflow Transitions (WorkflowViewMixin, apps/shared/workflow/mixins.py)
    // POST /api/v1/compliance/obligations/{id}/transition/
    // GET  /api/v1/compliance/obligations/{id}/transitions/available/
    // GET  /api/v1/compliance/obligations/{id}/transitions/history/
    // States: identified / registered / assessed / compliant / non_compliant
    // CMP-06/CMP-07: these two GET routes previously read
    // available_transitions/ / transition_history/, which do not exist on the
    // backend (WorkflowViewMixin registers transitions/available/ and
    // transitions/history/) -- WorkflowPanel's transition dropdown silently
    // rendered empty for every obligation as a result.
    // =========================================================================

    /**
     * Transition an obligation to a new workflow state.
     * Body: { to_state, comment?, evidence_ref? }
     */
    transitionObligation: builder.mutation({
      query: ({ id, to_state, comment = '', evidence_ref = '' }) => ({
        url: `/api/v1/compliance/obligations/${id}/transition/`,
        method: 'POST',
        body: { to_state, comment, evidence_ref },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Obligation', id },
        { type: 'Obligation', id: 'LIST' },
        { type: 'WorkflowTransition', id },
        { type: 'ComplianceStats' },
      ],
    }),

    /**
     * Get available transitions for an obligation from its current state.
     *
     * CMP-06/CMP-07: the raw response from WorkflowViewMixin.available_transitions
     * is `{ current_state, available_transitions: ['registered', ...], workflow_key }`
     * -- a wrapper object holding a bare list of to_state strings, not the
     * `[{to_state, label, required_evidence}]` shape WorkflowPanel (shared/ui,
     * dumb component) expects as its `available` prop. transformResponse
     * unwraps and shapes it. `required_evidence` isn't available per-to_state
     * from this endpoint (the registry only tracks it per from_state, and this
     * action doesn't return it) so it is conservatively defaulted to false --
     * this never blocks an advance, it only means the optional evidence-ref
     * input won't be force-shown. Tightening that is a follow-up, not part of
     * this fix (logged as CMP-08 in the ledger).
     */
    getObligationAvailableTransitions: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/obligations/${id}/transitions/available/` }),
      transformResponse: transformAvailableTransitions,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),

    /**
     * Get full transition history log for an obligation.
     *
     * CMP-06/CMP-07: WorkflowTransitionLogSerializer names its fields
     * triggered_by/triggered_at, but WorkflowPanel's HistoryRow reads
     * transitioned_by/transitioned_at -- transformResponse renames them so
     * the history table actually shows dates/actors instead of blank dashes.
     */
    getObligationTransitionHistory: builder.query({
      query: (id) => ({ url: `/api/v1/compliance/obligations/${id}/transitions/history/` }),
      transformResponse: transformTransitionHistory,
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),
  }),
});

export const {
  // Obligations
  useGetObligationsQuery,
  useGetObligationByIdQuery,
  useCreateObligationMutation,
  useUpdateObligationMutation,
  useDeleteObligationMutation,
  // Frameworks
  useGetFrameworksQuery,
  useGetFrameworkByIdQuery,
  useCreateFrameworkMutation,
  // Control Tests
  useGetControlTestsQuery,
  useCreateControlTestMutation,
  // Evidence (CMP-21)
  useGetObligationEvidenceQuery,
  useUploadEvidenceMutation,
  useDeleteEvidenceMutation,
  useDownloadEvidenceMutation,
  // Attestations (CMP-22)
  useGetAttestationsQuery,
  useGetMyAttestationsQuery,
  useCreateAttestationMutation,
  useCompleteAttestationMutation,
  useDeclineAttestationMutation,
  // Exceptions (CMP-22)
  useGetExceptionsQuery,
  useGetExceptionQuery,
  useCreateExceptionMutation,
  useUpdateExceptionMutation,
  useApproveExceptionMutation,
  useRejectExceptionMutation,
  useRevokeExceptionMutation,
  useResubmitExceptionMutation,
  useWithdrawExceptionMutation,
  // Stats
  useGetComplianceSummaryQuery,
  // Processing Activities
  useGetProcessingActivitiesQuery,
  useGetDsarsQuery,
  useGetDsarQuery,
  useCreateDsarMutation,
  useUpdateDsarMutation,
  useVerifyDsarMutation,
  useExtendDsarMutation,
  useRespondDsarMutation,
  useRefuseDsarMutation,
  useWithdrawDsarMutation,
  useCorrectDsarReceivedAtMutation,
  useGetBreachesQuery,
  useGetBreachQuery,
  useCreateBreachMutation,
  useUpdateBreachMutation,
  useDecideRegulatorMutation,
  useDecideSubjectsMutation,
  useSetAwareAtMutation,
  useRecordRegulatorNotifiedMutation,
  useRecordSubjectsNotifiedMutation,
  useDownloadBreachProofMutation,
  useCreateProcessingActivityMutation,
  // DPIAs
  useGetDPIAsQuery,
  useGetDPIAQuery,
  useSubmitDPIAMutation,
  useCreateDPIAMutation,
  useUpdateDPIAMutation,
  useApproveDPIAMutation,
  // Workflow
  useTransitionObligationMutation,
  useGetObligationAvailableTransitionsQuery,
  useGetObligationTransitionHistoryQuery,
} = complianceApi;

export default complianceApi;
